import {
    Alert,
    Button,
    Divider,
    Grid,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import toast from "react-hot-toast";
import DialogAddress, {AddressFormData} from "./DialogAddress";
import {Icon} from "@iconify/react";
import AddressFormContentData from "../AddressFormContentData";
import {SolicitationClientFormData} from "../../StepSuretiesData";
import {
    Client,
    ClientAddress, ClientAddressFields,
    ClientAddressTypes, ClientFields, ClientForm, ClientRelationshipInsert, ClientRelationshipInsertFields,
    ClientRelationshipTypeClassification, ClientRelationshipTypeClassificationFields,
    ClientRelationshipTypeFields,
    ClientRelationshipView, ClientRelationshipViewFields,
    ClientType
} from "src/types/client/clientData";
import {
    AsyncSelect,
    ControlledCheckBox,
    ControlledDatePicker,
    ControlledTextField
} from "../../../../../components/forms";
import ClientSummaryDialogCard
    from "../../../../../@core/layouts/components/horizontal/app-bar-content/ClientSummaryDialogCard";
import {NotRequiredSchema, RequiredSchema} from "../../../../../utils/validations";
import {LoaderBlockUI} from "../../../../../components/loader";
import {HttpClient, HttpClientAddress, HttpClientRelationships} from "../../../../../http/client/httpClient";
import {EntityWithIdFields} from "../../../../../types/baseEntities";
import {HttpCacheClient} from "../../../../../http/cache/client/httpCacheClient";
import {SolicitationClientFields} from "../../../../../types/solicitation/solicitationData";
import { BotonAgregar } from "src/pages/solicitation-new/components/Buttons";
import {DeleteIconButton} from "../../../../../components/buttons/Buttons";
import {HttpSolicitationClient} from "../../../../../http/solicitation/httpSolicitation";
import DialogAlert from "../../../../../components/dialog/DialogAlert";

interface DialogNewAfiliationFormProps {
    client: Client,
    onNewAffiliated: () => void,
    parentClientId: number,
    onBack: () => void,
    clientRelationshipLst: ClientRelationshipView[],
    clientClassificationCode: number | null,
    solicitationId?: number,
    onReload?: () => void
}


export enum ClientRelationshipTypeFormFields {
    RelationshipTypeCode = 'tipoRelacion',
    Signer = 'esFirmante',
    Address = 'direccion'
}

export interface ClientRelationshipTypeForm extends SolicitationClientFormData {
    [ClientRelationshipTypeFormFields.RelationshipTypeCode]: number,
    [ClientRelationshipTypeFormFields.Signer]: boolean,
    [ClientRelationshipTypeFormFields.Address]?: AddressFormData,
    clientRelationshipLst: ClientRelationshipInsert[];
}

const DialogNewAfiliationForm = ({client, onNewAffiliated, parentClientId, onBack, clientRelationshipLst, solicitationId, onReload, clientClassificationCode}: DialogNewAfiliationFormProps) => {
    const [relationshipTypes, setRelationshipTypes] = useState<ClientRelationshipTypeClassification[]>([]);
    const [errorNoRelationships, setErrorNoRelationships] = useState<boolean>(false)
    
    const [loading, setLoading] = useState<boolean>(true)
    const [editAddress, setEditAddress] = useState<boolean>(false)
    const [addressInDialog, setAddressInDialog] = useState<ClientAddress | undefined>(undefined)
    const [idx, setIdx] = useState<number>()
    const [relationshipCod, setRelationshipCod] = useState<number>()
    const [openDelete, setOpenDelete] = useState<boolean>(false)
    
    const onCloseDialog = () => {
        setEditAddress(false)
        setAddressInDialog(undefined)
    }

    const ClientFormSchema = useMemo(() => yup.object().shape({
        [ClientFields.IdentificationNumber]: NotRequiredSchema,
        [ClientFields.AfipResponsibilityTypeCode]: RequiredSchema
    }), [client])

    const methods = useForm<ClientRelationshipTypeForm>({
        resolver: yupResolver(ClientFormSchema),
        defaultValues: {
            [ClientFields.CUIT]: client[ClientFields.CUIT],
            [ClientFields.BusinessName]: client[ClientFields.BusinessName],
            [ClientFields.AfipResponsibilityTypeCode]: client[ClientFields.AfipResponsibilityTypeCode],
            [ClientFields.ClientClassificationCode]: client[ClientFields.ClientClassificationCode],
            [ClientFields.BirthDate]: client[SolicitationClientFields.ClientTypeCode] === ClientType.Fisica ? new Date(client[ClientFields.BirthDate]) : undefined
        }
    })

    const {fields, append, remove} = useFieldArray({
        name: "clientRelationshipLst",
        keyName: "key",
        control: methods.control
    })

    
    const loadRelationshipTypes = useCallback(() => {
        if (client) {
            HttpCacheClient.getRelacionTipos(1, clientClassificationCode)
                .then(relationshipList => {
                    setRelationshipTypes(relationshipList);
                })
        }
    }, [])
    const loadAddresses = () => {
        setLoading(true)
        HttpClientAddress.getByIdClient(client[EntityWithIdFields.Id]).then((r) => {
            const actualAddress = client[ClientFields.ClientTypeCode] === ClientType.Juridica ?
                r.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Legal)
                :
                r.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Real)
            methods.setValue(ClientRelationshipTypeFormFields.Address, actualAddress as AddressFormData)
        }).finally(() => setLoading(false))
    }

    useEffect(() => {
        loadRelationshipTypes();
        loadAddresses()
    }, [client])


    const onSubmitDialog = () => {
        toast.success("La dirección fue guardada con éxito")
        loadAddresses()
    }

    const onSubmit = (data: ClientRelationshipTypeForm) => {
        if (data.clientRelationshipLst.length !== 0) {
            const clientRelationships: ClientRelationshipInsert[] = data.clientRelationshipLst
    
            const clientData: ClientForm = {
                ...client,
                ...data
            }
            
            setLoading(true)
            
            const promises = []
    
            promises.push(HttpClient.update(clientData))
            promises.push(HttpClientRelationships.postRelacion(parentClientId, client[EntityWithIdFields.Id], clientRelationships))
                
    
            if (data[ClientRelationshipTypeFormFields.Address]) {
                const addressToInsert: AddressFormData = {
                    ...data[ClientRelationshipTypeFormFields.Address],
                    [ClientAddressFields.AddressTypeCode]: client[ClientFields.ClientTypeCode] === ClientType.Fisica ? ClientAddressTypes.Real : ClientAddressTypes.Legal,
                    [ClientAddressFields.CountryCode]: 1,
                    [ClientAddressFields.Verified]: true
                }
        
                !addressToInsert[ClientAddressFields.Id]
                    ? promises.push(HttpClientAddress.insert(client[EntityWithIdFields.Id], addressToInsert as ClientAddress))
                    : promises.push(HttpClientAddress.update(client[EntityWithIdFields.Id], addressToInsert as ClientAddress))
            }
            
            Promise.all(promises).then(() => {
                        toast.success("El socio se ha guardado correctamente")
                        onNewAffiliated()
                    }).catch(() => {
                        toast.error('Ocurrió un error')
                    }).finally(() => setLoading(false))
        } else {
            setErrorNoRelationships(true)
        }
    }
    
    const loadResponsibilityTypes = useCallback(() => {
        return HttpCacheClient.getResponsableTipo();
    }, [])

    const loadClientClassifications = useCallback(() => {
        return HttpCacheClient.getClienteClasificaciones()
    }, [])

    useEffect(() => {
        if (clientRelationshipLst && clientRelationshipLst.length > 0) {
            remove();
            const toAppend = clientRelationshipLst.map((relationship: any) => {
                return {
                    tipoRelacion: relationship[SolicitationClientFields.RelationshipTypeCode],
                    clasificacionRelacion: relationship[SolicitationClientFields.ClientClassificationCode] || relationship[ClientRelationshipViewFields.RelationshipClassificationCode],
                    porcParticipacion: relationship[SolicitationClientFields.Participation] || relationship[ClientRelationshipViewFields.Participation],
                    esFirmante: relationship[ClientRelationshipViewFields.Signer]
                } as ClientRelationshipInsert
            });
            append(toAppend);
        }
    }, [clientRelationshipLst, append, remove]);
    
    const onDeleteItem = (relationshipType: number, curidx: number) => {
        setRelationshipCod(relationshipType)
        setIdx(curidx)
        setOpenDelete(true)
    }
    
    const handleDeleteItem = () => {
        if (!!idx || idx === 0) {
            const currentRelationship = clientRelationshipLst.find((relationship: ClientRelationshipView) =>
                relationship[ClientRelationshipViewFields.RelationshipTypeCode] === relationshipCod)
            if (currentRelationship?.id) {
                if (solicitationId) {
                    HttpSolicitationClient.deleteClientRelated(solicitationId, currentRelationship.id).then(onReload)
                } else {
                    HttpClientRelationships.delete(client[EntityWithIdFields.Id], currentRelationship.id).then(onReload)
                }
            }
            remove(idx)
            setOpenDelete(false)
            setRelationshipCod(undefined)
        }
    }
    
    const handleCloseDelete = () => {
        setOpenDelete(false)
        setRelationshipCod(undefined)
        setIdx(undefined)
    }
    
    return (
        loading ? <LoaderBlockUI />
            :
                <Fragment>
                    <Grid item xs={12}>
                        <Typography fontSize={16} fontWeight={600}>Datos del accionista</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider/>
                    </Grid>
                    <Grid item xs={12}>
                        <ClientSummaryDialogCard client={client} action={() => {setLoading(false)}}/>
                    </Grid>
                    <Grid item md={1}></Grid>
                    <Grid item xs={12} container spacing={3} mt={2}>
                        <Grid item xs={12} md={6}>
                            <AsyncSelect control={methods.control} label="Responsabilidad AFIP"
                                         loadOptions={loadResponsibilityTypes}
                                         defaultValue={client?.[ClientFields.AfipResponsibilityTypeCode]}
                                         name={ClientFields.AfipResponsibilityTypeCode}
                                         fullWidth
                            />
                        </Grid>
                        {
                            client[ClientFields.ClientTypeCode] === ClientType.Juridica &&
                            <Grid item md={6}>
                                <AsyncSelect control={methods.control}
                                             name={ClientFields.ClientClassificationCode}
                                             defaultValue={client?.[ClientFields.ClientClassificationCode]}
                                             label="Tipo Societario" loadOptions={loadClientClassifications}
                                             fullWidth
                                />
                            </Grid>
                        }
                        {
                            client[ClientFields.ClientTypeCode] === ClientType.Fisica &&
                            <Grid item md={4}>
                                <ControlledDatePicker control={methods.control}
                                                      label="Fecha Nacimiento"
                                                      name={ClientFields.BirthDate}
                                />
                            </Grid>
                        }
                    </Grid>
                    <Grid item md={2}></Grid>
                    <Grid item xs={12}>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Typography fontSize={18} fontWeight={600}>Relaciones del cliente</Typography>
                            <BotonAgregar onClick={() => {
                                if (relationshipTypes && fields.length < relationshipTypes.length) {
                                    append({
                                        tipoRelacion: relationshipTypes[0][ClientRelationshipTypeClassificationFields.ClientRelationshipTypeCode],
                                        clasificacionRelacion: relationshipTypes[0][ClientRelationshipTypeClassificationFields.ClientClassificationCode],
                                        porcParticipacion: 0,
                                        esFirmante: false

                                    } as ClientRelationshipInsert)
                                }
                            }}/>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        {errorNoRelationships && fields.length === 0 &&
                            <Alert severity={'error'}>
                                Debes cargar al menos una relación para guardar
                            </Alert>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        Tipo Relación
                                    </TableCell>
                                    <TableCell>
                                        Participación
                                    </TableCell>
                                    <TableCell>
                                        Firmante
                                    </TableCell>
                                    <TableCell>
                                        Eliminar
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {

                                    fields.map((field, index) => {
                                        return (
                                            <TableRow key={field.key}>
                                                <TableCell width="40%">
                                                    <ControlledTextField control={methods.control}
                                                                         style={{marginBottom: 0}}
                                                                         label={"Tipo Relación"}
                                                                         options={relationshipTypes} select
                                                                         name={`clientRelationshipLst.${index}.${ClientRelationshipInsertFields.RelationshipTypeCode}` as const}
                                                    />
                                                </TableCell>
                                                <TableCell width="30%">
                                                    {
                                                        <ControlledTextField control={methods.control}
                                                                             label={"Participación"}
                                                                             style={{marginBottom: 0}}
                                                                             defaultValue={field.porcParticipacion}
                                                                             name={`clientRelationshipLst.${index}.${ClientRelationshipInsertFields.Participation}` as const}
                                                                             InputProps={{
                                                                                 endAdornment: <InputAdornment position={'start'}>%</InputAdornment>
                                                                             }}
                                                                             disabled={!relationshipTypes.find(relationship => relationship[ClientRelationshipTypeFields.Id] === methods.watch(`clientRelationshipLst.${index}.tipoRelacion` as const))
                                                                               ?.[ClientRelationshipTypeFields.EditParticipation]}
                                                        />
                                                    }
                                                </TableCell>
                                                <TableCell width="15%">
                                                        <ControlledCheckBox name={`clientRelationshipLst.${index}.esFirmante`} 
                                                                            control={methods.control} 
                                                                            label={"Firmante"}
                                                                            disabled={!relationshipTypes.find(relationship => relationship[ClientRelationshipTypeFields.Id] === methods.watch(`clientRelationshipLst.${index}.tipoRelacion` as const))
                                                                                ?.[ClientRelationshipTypeFields.EditSigner]}
                                                        />
                                                </TableCell>
                                                <TableCell width="15%">
                                                    <DeleteIconButton onClick={() => onDeleteItem(field.tipoRelacion, index)}/>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                }
                            </TableBody>
                        </Table>
                    </Grid>
                    <Grid item xs={12} mt={5}>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Typography fontSize={18} fontWeight={600}>{`Domicilio ${client[ClientFields.ClientTypeCode] === ClientType.Fisica ? 'real' : 'legal'}`}</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider/>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        {!loading &&
                            <FormProvider {...methods}>
                                <AddressFormContentData nameBase={ClientRelationshipTypeFormFields.Address}/>
                            </FormProvider>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Button color='secondary' variant='contained' onClick={onBack} startIcon={<Icon icon='mdi:chevron-left' fontSize={20}/>}>Atrás</Button>
                            <Button color='primary' variant='contained' onClick={methods.handleSubmit(onSubmit)}>Guardar</Button>
                        </Stack>
                    </Grid>
                    <DialogAddress
                        open={editAddress}
                        setEditAddress={setEditAddress}
                        renderAddressTitle={() => `Editar domicilio ${client[ClientFields.ClientTypeCode] === ClientType.Fisica ? 'real' : 'legal'}`}
                        address={addressInDialog && addressInDialog}
                        onClose={onCloseDialog}
                        onSubmitDialog={onSubmitDialog}
                        clientId={client[EntityWithIdFields.Id]}
                        addressType={client[ClientFields.ClientTypeCode] === ClientType.Fisica ? ClientAddressTypes.Real : ClientAddressTypes.Legal}
                    />
                    <DialogAlert open={openDelete}
                                 onClose={handleCloseDelete}
                                 title={'Eliminar relación'}
                                 subtitle={'¿Estás seguro de querer eliminar esta relación?'}
                                 onAccept={handleDeleteItem}
                    />
                </Fragment>
    )
}


export default DialogNewAfiliationForm